import { cn } from '@/lib/utils';

export type WizardStepBarStep<Id extends string | number> = {
  id: Id;
  label: string;
  /** Second line, e.g. a live summary of what the step currently holds. */
  subtitle?: string;
  complete?: boolean;
};

/**
 * Tailwind cannot take an interpolated class name, so the column count is
 * looked up rather than built from `steps.length`.
 */
const COLUMNS: Record<number, string> = {
  2: 'tw:grid-cols-2',
  3: 'tw:grid-cols-3',
  4: 'tw:grid-cols-2 tw:sm:grid-cols-4',
  5: 'tw:grid-cols-2 tw:sm:grid-cols-3 tw:lg:grid-cols-5',
  6: 'tw:grid-cols-2 tw:sm:grid-cols-3 tw:lg:grid-cols-6',
  7: 'tw:grid-cols-2 tw:sm:grid-cols-3 tw:lg:grid-cols-7',
  8: 'tw:grid-cols-2 tw:sm:grid-cols-4 tw:lg:grid-cols-8',
};

const BUTTON =
  'tw:cursor-pointer tw:rounded-md tw:border tw:border-border tw:bg-transparent tw:px-2 tw:py-2 tw:text-center tw:text-xs tw:text-foreground tw:transition-colors tw:hover:bg-muted/40 tw:focus-visible:[outline:1px_solid_var(--ring)] tw:focus-visible:outline-offset-[-1px]';

/**
 * Horizontal wizard steps. Every step is reachable at any time, including ones
 * ahead of the active step, so callers stay responsible for validating before
 * they act on the collected values.
 */
export function WizardStepBar<Id extends string | number>({
  steps,
  activeId,
  onStepChange,
  label,
  className,
}: {
  steps: readonly WizardStepBarStep<Id>[];
  activeId: Id;
  onStepChange: (id: Id) => void;
  /** Accessible name for the step list. */
  label: string;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={className}>
      <ol
        className={cn(
          'tw:m-0 tw:grid tw:list-none tw:gap-2 tw:p-0',
          COLUMNS[steps.length] ?? 'tw:grid-cols-2 tw:sm:grid-cols-4'
        )}
      >
        {steps.map((step) => {
          const isActive = step.id === activeId;
          return (
            <li key={step.id}>
              <button
                type="button"
                aria-current={isActive ? 'step' : undefined}
                onClick={() => onStepChange(step.id)}
                className={cn(
                  BUTTON,
                  'tw:w-full',
                  step.complete && 'tw:border-emerald-500 tw:bg-emerald-500/10',
                  isActive &&
                    'tw:border-primary tw:bg-primary/10 tw:font-medium'
                )}
              >
                <span className="tw:block tw:truncate">{step.label}</span>
                {step.subtitle ? (
                  <span
                    title={step.subtitle}
                    className="tw:mt-0.5 tw:block tw:truncate tw:font-normal tw:text-muted-foreground"
                  >
                    {step.subtitle}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
